import {
    IconAdjustments,
    IconRectangle,
    IconRectangleVertical,
    IconWand,
} from '@tabler/icons-react';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import Hint from '../../../../../components/common/Hint';
import {
    MARGIN_OPTIONS,
    ORIENTATION_OPTIONS,
    PAGE_SIZE_OPTIONS,
} from '../../pdf/create/createPdfEngine';

/** Tile in an icon tray: glyph over label, same shape as the other image tools. */
function tileBtnClass(active) {
    return `flex min-h-[54px] flex-col items-center justify-center gap-1 rounded-lg border-none px-1 py-1.5 text-[11px] leading-tight transition-all ${
        active
            ? 'bg-white font-semibold text-pdf-cat-solid shadow-[0_1px_2px_rgba(28,24,21,0.04)] dark:bg-zinc-900 dark:text-orange-400'
            : 'bg-transparent font-medium text-[#6B635C] dark:text-zinc-400'
    }`;
}

const ORIENTATION_ICONS = {
    auto: IconWand,
    portrait: IconRectangleVertical,
    landscape: IconRectangle,
};

/** Paper proportions, so A5 reads as smaller than A3 rather than as a word. */
const PAGE_SIZE_RATIO = {
    a4: 595.28 / 841.89,
    letter: 612 / 792,
    a3: 841.89 / 1190.55,
    legal: 612 / 1008,
    a5: 419.53 / 595.28,
};

/** Relative footprint on the tile — A3 is the biggest sheet offered, A5 the smallest. */
const PAGE_SIZE_SCALE = { a3: 1, legal: 0.92, a4: 0.86, letter: 0.84, a5: 0.68 };

function PageSizeGlyph({ id, active }) {
    const color = active ? 'currentColor' : '#B4B0AA';
    const H = 22 * (PAGE_SIZE_SCALE[id] ?? 0.86);
    const W = H * (PAGE_SIZE_RATIO[id] ?? 0.707);
    return (
        <span className="flex h-[22px] w-[22px] items-center justify-center">
            <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
                <rect x="0.8" y="0.8" width={W - 1.6} height={H - 1.6} rx="1.5" fill="none" stroke={color} strokeWidth="1.5" />
            </svg>
        </span>
    );
}

function SectionHeader({ title, hint }) {
    return (
        <div className="mb-2 flex items-center justify-between gap-2">
            <p className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">{title}</p>
            {hint ? (
                <span className="text-[10.5px] font-medium uppercase tracking-wide text-[#A8A099] dark:text-zinc-500">
                    {hint}
                </span>
            ) : null}
        </div>
    );
}

function MarginIcon({ inset }) {
    const pad = { none: 0, small: 3, medium: 5, large: 7 }[inset] ?? 5;
    return (
        <svg width="22" height="28" viewBox="0 0 22 28" aria-hidden="true">
            <rect x="1" y="1" width="20" height="26" rx="2" fill="none" stroke="currentColor" strokeWidth="1.2" />
            <rect
                x={pad}
                y={pad + 2}
                width={22 - pad * 2}
                height={26 - pad * 2 - 2}
                rx="1"
                fill="currentColor"
                opacity="0.18"
            />
        </svg>
    );
}

export default function ToPdfImageSettings({ settings, onChange }) {
    const { t } = useTranslation();

    const update = (patch) => {
        onChange({ ...settings, ...patch });
    };

    return (
        <div className="flex flex-col rounded-[20px] border border-[#ECE8E4] bg-white shadow-[0_1px_2px_rgba(28,24,21,0.04)] dark:border-zinc-700 dark:bg-zinc-900">
            <div className="border-b border-[#ECE8E4] px-4 py-3.5 dark:border-zinc-700">
                <div className="flex items-start gap-2.5">
                    <div className="flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-pdf-cat-tint text-pdf-cat-solid dark:bg-orange-950/30 dark:text-orange-400">
                        <IconAdjustments size={18} stroke={1.8} />
                    </div>
                    <div className="min-w-0">
                        <p className="text-[13px] font-semibold text-[#3D3833] dark:text-zinc-200">
                            {t('createPdf.settings.title')}
                        </p>
                        <p className="text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                            {t('createPdf.settings.subtitle')}
                        </p>
                    </div>
                </div>
            </div>

            {/* No inner scroll: the panel is as tall as its tab needs and the page
                scrolls, so nothing is hidden behind a scrollbar of its own. */}
            <div className="px-4 py-3.5">
                <div className="space-y-4">
                        <div>
                            <SectionHeader
                                title={t('createPdf.settings.orientationTitle')}
                                hint={t('createPdf.settings.orientationHint')}
                            />
                            <div className="flex gap-1 rounded-[11px] bg-[#F7F5F3] p-1 dark:bg-zinc-800">
                                {ORIENTATION_OPTIONS.map((id) => {
                                    const Icon = ORIENTATION_ICONS[id] || IconWand;
                                    const label = t(`createPdf.settings.orientations.${id}`);
                                    return (
                                        <Hint label={label} key={id}>
                                            <button
                                                type="button"
                                                onClick={() => update({ orientation: id })}
                                                aria-label={label}
                                                aria-pressed={settings.orientation === id}
                                                className={`${tileBtnClass(settings.orientation === id)} flex-1`}
                                            >
                                                <Icon size={20} stroke={1.8} />
                                            </button>
                                        </Hint>
                                    );
                                })}
                            </div>
                        </div>

                        <div>
                            <SectionHeader
                                title={t('createPdf.settings.pageSizeTitle')}
                                hint={t('createPdf.settings.pageSizeHint')}
                            />
                            <div className="grid grid-cols-3 gap-1 rounded-[11px] bg-[#F7F5F3] p-1 dark:bg-zinc-800">
                                {PAGE_SIZE_OPTIONS.map((id) => (
                                    <Hint label={t(`createPdf.settings.pageSizes.${id}`)} key={id}>
                                        <button
                                            type="button"
                                            onClick={() => update({ pageSize: id })}
                                            className={`${tileBtnClass(settings.pageSize === id)}`}
                                        >
                                            <PageSizeGlyph id={id} active={settings.pageSize === id} />
                                            {t(`createPdf.settings.pageSizes.${id}`)}
                                        </button>
                                    </Hint>
                                ))}
                            </div>
                        </div>

                        <div>
                            <SectionHeader
                                title={t('createPdf.settings.marginTitle')}
                                hint={t('createPdf.settings.marginHint')}
                            />
                            <div className="grid grid-cols-4 gap-2">
                                {/* Glyphs only — each draws its own margin, and the
                                    name is in the tooltip / aria-label. */}
                                {MARGIN_OPTIONS.map((id) => (
                                    <Hint label={t(`createPdf.settings.margins.${id}`)} key={id}>
                                    <button
                                        type="button"
                                        onClick={() => update({ margin: id })}
                                        aria-label={t(`createPdf.settings.margins.${id}`)}
                                        aria-pressed={settings.margin === id}
                                        className={`flex flex-col items-center gap-1 rounded-xl border px-2 py-2 transition-colors ${
                                            settings.margin === id
                                                ? 'border-pdf-cat-solid bg-pdf-cat-tint text-pdf-cat-solid dark:border-orange-500 dark:bg-orange-950/30 dark:text-orange-400'
                                                : 'border-[#ECE8E4] text-[#6B635C] hover:border-pdf-cat-solid/30 dark:border-zinc-700 dark:text-zinc-400'
                                        }`}
                                    >
                                        <MarginIcon inset={id} />
                                    </button>
                                    </Hint>
                                ))}
                            </div>
                        </div>

                        <div className="flex items-center justify-between gap-3 rounded-xl border border-[#ECE8E4] bg-[#FAFAF9] px-3 py-3 dark:border-zinc-700 dark:bg-zinc-800/50">
                            <div className="min-w-0 flex-1">
                                <p className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                    {t('createPdf.settings.pageNumbersTitle')}
                                </p>
                                <p className="text-[11px] text-[#A8A099] dark:text-zinc-500">
                                    {t('createPdf.settings.pageNumbersHint')}
                                </p>
                            </div>
                            <button
                                type="button"
                                role="switch"
                                aria-checked={settings.pageNumbers}
                                onClick={() => update({ pageNumbers: !settings.pageNumbers })}
                                className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${
                                    settings.pageNumbers
                                        ? 'bg-pdf-cat-solid dark:bg-orange-500'
                                        : 'bg-[#ECE8E4] dark:bg-zinc-700'
                                }`}
                            >
                                <span
                                    className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
                                        settings.pageNumbers ? 'translate-x-5' : 'translate-x-0'
                                    }`}
                                />
                            </button>
                        </div>
                </div>
            </div>
        </div>
    );
}

